import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { proposalService } from "../../services/proposals";
import Table from "../common/Table";
import Button from "../common/Button";
import { Download } from "lucide-react";
import toast from "react-hot-toast";

const ProposalView = ({ onEdit, onDelete }) => {
  const { data: proposals, isLoading } = useQuery({
    queryKey: ["proposals"],
    queryFn: proposalService.getAll,
  });
  const [pdfDropdown, setPdfDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setPdfDropdown(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const columns = [
    { header: "Title", accessor: "title" },
    { header: "Client", accessor: (row) => row.client?.name },
    { header: "Total Amount", accessor: (row) => `₹${row.totalAmount}` },
    { header: "Status", accessor: "status" },
  ];

  const handleDownloadPDF = async (id, includeSignature) => {
    setPdfDropdown(null);
    try {
      const blob = await proposalService.downloadPDF(id, includeSignature);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `proposal-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const actions = (row) => (
    <>
      <div className="relative inline-block" ref={pdfDropdown === row._id ? dropdownRef : null}>
        <Button
          variant="secondary"
          className="mr-2"
          onClick={() => setPdfDropdown(pdfDropdown === row._id ? null : row._id)}
        >
          <Download size={14} className="inline mr-1" />
          PDF
        </Button>
        {pdfDropdown === row._id && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
            <p className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">Include signature?</p>
            <button
              onClick={() => handleDownloadPDF(row._id, true)}
              className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Yes, with signature
            </button>
            <button
              onClick={() => handleDownloadPDF(row._id, false)}
              className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 transition-colors"
            >
              No, without signature
            </button>
          </div>
        )}
      </div>
      <Button
        variant="secondary"
        className="mr-2"
        onClick={() => onEdit(row)}
      >
        Edit
      </Button>
      <Button
        variant="danger"
        onClick={() => onDelete(row._id)}
      >
        Delete
      </Button>
    </>
  );

  if (isLoading) return <div>Loading...</div>;

  return <Table columns={columns} data={proposals} actions={actions} />;
};

export default ProposalView;
